import { useEffect, useRef, useCallback, useState } from "react";
import { useMsalAuth } from "./useMsalAuth";
import { useNotification } from "@/common/hooks/useNotification";
import { NotificationType } from "@/components/Notifications/Notification.type";

/**
 * In-app notification data structure from server
 */
interface AppNotification {
  id: string;
  type: "new_lead" | "chat_message" | "tour_booked" | "escalation" | "info" | "warning";
  title: string;
  body: string;
  link?: string;
  linkText?: string;
  icon?: "person" | "chat" | "calendar" | "info" | "warning";
  timestamp: string;
  dismissable?: boolean;
  autoHideMs?: number;
}

interface WebSocketMessage {
  type: "connected" | "notification" | "error";
  data?: AppNotification;
  instanceId?: string;
  userId?: string;
  message?: string;
}

interface UseNotificationWebSocketOptions {
  /** Called when a notification is received (before showing toast) */
  onNotification?: (notification: AppNotification) => void;
  /** Called when connection status changes */
  onConnectionChange?: (connected: boolean) => void;
  /** Whether to show toast notifications (default: true) */
  showToast?: boolean;
}

/**
 * Hook to connect to the notification WebSocket and receive real-time in-app notifications.
 *
 * @param instanceId - The instance ID to receive notifications for
 * @param options - Optional callbacks for notification events
 */
export function useNotificationWebSocket(
  instanceId: string | undefined,
  options: UseNotificationWebSocketOptions = {},
) {
  const { getAccessToken, isLoggedIn } = useMsalAuth();
  const { addNotification } = useNotification();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const optionsRef = useRef(options);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  const isMountedRef = useRef(false);
  const addNotificationRef = useRef(addNotification);
  const maxReconnectAttempts = 5;

  // Keep refs updated
  optionsRef.current = options;
  addNotificationRef.current = addNotification;

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, "Client disconnecting");
      wsRef.current = null;
    }
    isConnectingRef.current = false;
    setIsConnected(false);
  }, []);

  const connect = useCallback(async () => {
    if (!instanceId || !isLoggedIn) {
      return;
    }

    // Don't create duplicate connections - check both OPEN and CONNECTING states
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING ||
      isConnectingRef.current
    ) {
      return;
    }

    isConnectingRef.current = true;

    try {
      const token = await getAccessToken();
      if (!token) {
        console.warn("[NotificationWS] No access token available");
        isConnectingRef.current = false;
        return;
      }

      // Check again after async token fetch
      if (!isMountedRef.current) {
        isConnectingRef.current = false;
        return;
      }

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${protocol}//${window.location.host}/ws/notifications?instanceId=${encodeURIComponent(instanceId)}&token=${encodeURIComponent(token)}`;

      console.log("[NotificationWS] Connecting...");
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("[NotificationWS] Connected");
        isConnectingRef.current = false;
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        optionsRef.current.onConnectionChange?.(true);
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          if (message.type === "notification" && message.data) {
            const notification = message.data;

            // Call optional callback
            optionsRef.current.onNotification?.(notification);

            // Show toast notification if not disabled
            if (optionsRef.current.showToast !== false) {
              // Determine notification type for toast
              let notifType: NotificationType = NotificationType.INFO;
              if (notification.type === "warning" || notification.type === "escalation") {
                notifType = NotificationType.WARNING;
              }

              // Show toast notification using ref to avoid stale closure
              addNotificationRef.current({
                type: notifType,
                title: notification.title,
                message: notification.body,
                timeout: notification.autoHideMs || 8000,
                link: notification.link,
                linkText: notification.linkText,
              });
            }
          } else if (message.type === "connected") {
            console.log("[NotificationWS] Authenticated", {
              instanceId: message.instanceId,
              userId: message.userId,
            });
          } else if (message.type === "error") {
            console.error("[NotificationWS] Error:", message.message);
          }
        } catch (error) {
          console.error("[NotificationWS] Failed to parse message:", error);
        }
      };

      ws.onclose = (event) => {
        console.log("[NotificationWS] Disconnected", { code: event.code, reason: event.reason });
        isConnectingRef.current = false;
        setIsConnected(false);
        optionsRef.current.onConnectionChange?.(false);
        wsRef.current = null;

        // Attempt to reconnect if not a normal close and component still mounted
        if (
          event.code !== 1000 &&
          isMountedRef.current &&
          reconnectAttemptsRef.current < maxReconnectAttempts
        ) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectAttemptsRef.current++;
          console.log(
            `[NotificationWS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`,
          );
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = (error) => {
        console.error("[NotificationWS] WebSocket error:", error);
        isConnectingRef.current = false;
      };
    } catch (error) {
      console.error("[NotificationWS] Failed to connect:", error);
      isConnectingRef.current = false;
    }
  }, [instanceId, isLoggedIn, getAccessToken]);

  // Connect when instanceId changes and user is logged in
  useEffect(() => {
    isMountedRef.current = true;

    if (instanceId && isLoggedIn) {
      connect();
    }

    return () => {
      isMountedRef.current = false;
      disconnect();
    };
  }, [instanceId, isLoggedIn, connect, disconnect]);

  return {
    isConnected,
    disconnect,
    reconnect: connect,
  };
}

export default useNotificationWebSocket;
